import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import type { Stripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripe";
import BillingDetailsForm, { type BillingCustomer } from "./BillingDetailsForm";
import PaymentMethodsList, { type PaymentMethod } from "./PaymentMethodsList";
import SmsUsageCard from "./SmsUsageCard";
import SubscriptionSummaryCard from "./SubscriptionSummaryCard";


const SubscriptionTab = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
  const [stripe, setStripe] = useState<Promise<Stripe | null> | null>(null);

  const [billingCustomer, setBillingCustomer] = useState<BillingCustomer | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [defaultPmId, setDefaultPmId] = useState<string | null>(null);
  const [hasActiveSub, setHasActiveSub] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      await supabase.functions.invoke("check-subscription");
    } catch (e: any) {
      toast.error(`Could not load subscription: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshBilling = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("manage-billing", {
        body: { action: "get_billing" },
      });
      if (error) throw error;
      setBillingCustomer(data.customer);
      setPaymentMethods(data.payment_methods || []);
      setDefaultPmId(data.default_pm_id || null);
      setHasActiveSub(!!data.has_active_subscription);
    } catch (e: any) {
      console.warn("billing fetch failed", e.message);
    }
  };

  useEffect(() => {
    refresh();
    refreshBilling();
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscription") === "success") {
      toast.success("Subscription reactivated.");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => { refresh(); refreshBilling(); }, 1500);
    }
    if (params.get("sms_topup") === "success") {
      toast.success("SMS top-up successful — credits added to your account.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("sms_topup") === "cancelled") {
      toast.info("SMS top-up cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleCancelReactivate = async (action: "cancel" | "reactivate") => {
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-subscription", { body: { action } });
      if (error) throw error;
      toast.success(
        action === "cancel"
          ? "Subscription will end at the end of your current period."
          : "Subscription reactivated.",
      );
      await refresh();
    } catch (e: any) {
      toast.error(`${action === "cancel" ? "Cancel" : "Reactivate"} failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubscribe = async () => {
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-subscription", {
        body: { action: "resubscribe" },
      });
      if (error) throw error;
      if (data?.needs_checkout && data?.client_secret) {
        const s = await getStripe();
        setStripe(Promise.resolve(s));
        setCheckoutSecret(data.client_secret);
      } else if (data?.success) {
        toast.success("Subscription reactivated.");
        await refresh();
      }
    } catch (e: any) {
      toast.error(`Reactivate failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const fetchClientSecret = useCallback(async () => checkoutSecret!, [checkoutSecret]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <SubscriptionSummaryCard
        showManageButton={false}
        showPrice={true}
        onCancel={() => handleCancelReactivate("cancel")}
        onReactivate={() => handleCancelReactivate("reactivate")}
        onResubscribe={handleResubscribe}
        actionLoading={actionLoading}
      />

      {checkoutSecret && stripe && (
        <Card>
          <CardContent className="pt-6">
            <h4 className="font-medium mb-2">Enter payment details</h4>
            <EmbeddedCheckoutProvider stripe={stripe} options={{ fetchClientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </CardContent>
        </Card>
      )}

      <SmsUsageCard />

      <BillingDetailsForm customer={billingCustomer} onSaved={refreshBilling} />
      <PaymentMethodsList
        paymentMethods={paymentMethods}
        defaultPmId={defaultPmId}
        hasActiveSubscription={hasActiveSub}
        onChanged={refreshBilling}
      />
    </div>
  );
};

export default SubscriptionTab;
